import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Brain, Cpu, Network, Shield, BarChart3, Eye, Mic, GitBranch, ArrowRight, Plane, Crosshair, Radio } from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const fadeIn = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const AI_SERVICES = [
  {
    icon: Brain,
    title: 'Strategic AI Consulting',
    desc: 'We assess your enterprise readiness, design AI roadmaps, and guide your transformation from legacy systems to intelligent operations.'
  },
  {
    icon: Eye,
    title: 'Computer Vision',
    desc: 'Object detection, image classification, facial recognition, and visual inspection systems for manufacturing, defense, and satellite imagery.'
  },
  {
    icon: Mic,
    title: 'Natural Language Processing',
    desc: 'Multi-language document analysis, sentiment detection, intelligent search, and automated report generation across 47+ languages.'
  }
];

const ML_SERVICES = [
  {
    icon: BarChart3,
    title: 'Predictive Analytics',
    desc: 'Forecast equipment failures, market trends, and operational anomalies before they happen with 96%+ accuracy.'
  },
  {
    icon: GitBranch,
    title: 'MLOps & Infrastructure',
    desc: 'End-to-end ML pipelines with automated training, deployment, monitoring, and versioning at enterprise scale.'
  },
  {
    icon: Cpu,
    title: 'Edge AI Deployment',
    desc: 'Optimized models running on IoT devices, drones, and embedded systems for real-time decision-making without cloud dependency.'
  }
];

const DL_SERVICES = [
  {
    icon: Network,
    title: 'Custom Neural Architectures',
    desc: 'Bespoke deep learning models designed for your specific data patterns and performance requirements.'
  },
  {
    icon: Shield,
    title: 'Secure AI Systems',
    desc: 'Adversarial-robust models with built-in security, bias detection, and compliance for defense and government applications.'
  }
];

const DRONE_SERVICES = [
  {
    icon: Plane,
    title: 'Bespoke UAV Design & Manufacturing',
    desc: 'Custom-built unmanned aerial vehicles engineered for specific mission profiles\u2014from long-endurance surveillance platforms to high-speed tactical drones. Hardware and software, designed in-house.'
  },
  {
    icon: Crosshair,
    title: 'Autonomous Navigation & AI Flight Systems',
    desc: 'GPS-denied navigation, obstacle avoidance, swarm coordination, and autonomous mission execution. Our AI flight stack enables fully autonomous operations in contested environments.'
  },
  {
    icon: Radio,
    title: 'Satellite + Drone Intelligence Fusion',
    desc: 'We stitch satellite imagery and drone sensor data into a unified intelligence layer\u2014extracting maximum situational awareness by correlating multi-altitude, multi-spectral data sources in real-time.'
  }
];

function ServiceBlock({ title, subtitle, services, accent }) {
  return (
    <div className="py-14 md:py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div {...fadeUp} className="mb-10 md:mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-brand-accent font-bold mb-4">{subtitle}</p>
          <h2 className="text-4xl md:text-5xl font-heading font-bold tracking-tight">{title}</h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {services.map((service, i) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className={`glass-card rounded-lg p-8 lg:p-10 group ${
                services.length === 3
                  ? i === 0 ? 'md:col-span-7' : i === 1 ? 'md:col-span-5' : 'md:col-span-12'
                  : i === 0 ? 'md:col-span-8' : 'md:col-span-4'
              }`}
            >
              <service.icon className="w-8 h-8 text-brand-accent mb-6 opacity-70 group-hover:opacity-100 transition-opacity duration-300" />
              <h3 className="text-xl md:text-2xl font-heading font-semibold mb-3">{service.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{service.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      {/* Header */}
      <section className="pt-24 pb-4 md:pb-8 relative">
        <div className="absolute inset-0 bg-surface grid-bg" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.p {...fadeIn} className="text-xs uppercase tracking-[0.3em] text-brand-accent font-bold mb-4 md:mb-6">
            Our Capabilities
          </motion.p>
          <motion.h1
            {...fadeIn}
            transition={{ ...fadeIn.transition, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-heading font-black tracking-tight leading-[0.9] mb-6 md:mb-8"
          >
            What We
            <br />
            <span className="text-gradient-cyan">Build</span>
          </motion.h1>
          <motion.p
            {...fadeIn}
            transition={{ ...fadeIn.transition, delay: 0.2 }}
            className="text-lg text-slate-400 font-light max-w-xl"
          >
            From strategic consulting to production deployment, we cover the full spectrum of AI, Machine Learning, and Deep Learning.
          </motion.p>
        </div>
      </section>

      <ServiceBlock
        title="AI Solutions"
        subtitle="Artificial Intelligence"
        services={AI_SERVICES}
      />

      <div className="border-t border-white/5" />

      <ServiceBlock
        title="ML Infrastructure"
        subtitle="Machine Learning"
        services={ML_SERVICES}
      />

      <div className="border-t border-white/5" />

      <ServiceBlock
        title="Deep Learning R&D"
        subtitle="Neural Networks"
        services={DL_SERVICES}
      />

      <div className="border-t border-white/5" />

      <ServiceBlock
        title="Drones & Unmanned Systems"
        subtitle="UAV & UGV Platforms"
        services={DRONE_SERVICES}
      />

      {/* CTA */}
      <section className="py-16 md:py-32 border-t border-white/5 section-glow">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-4xl md:text-5xl font-heading font-bold tracking-tight mb-6">
              Have a Challenge in Mind?
            </h2>
            <p className="text-lg text-slate-400 font-light max-w-lg mx-auto mb-12">
              Tell us about your problem. We&apos;ll tell you how AI can solve it.
            </p>
            <Link
              to="/contact"
              data-testid="services-cta"
              className="inline-flex items-center gap-2 bg-white text-black font-bold text-sm uppercase tracking-wider px-10 py-5 hover:bg-slate-200 transition-colors duration-200"
            >
              Get in Touch <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
